import React, { Component } from 'react';
import PropTypes from 'prop-types';
import RenderError from '@ali/vu-render-error';
import {
  log, warn, tryRender, isVisibleOnDevice, getJoinKey,
} from '@ali/re-utils';
import XProps from './XProps';

class Wrapper extends Component {
  static displayName = 'Wrapper';

  static propTypes = {
    store: PropTypes.object,
    action: PropTypes.object,
    context: PropTypes.object.isRequired,
    pageId: PropTypes.string,
    componentSchema: PropTypes.object,
    parentXprops: PropTypes.object,
    __iteratorId: PropTypes.string,
    __ownerScope: PropTypes.object,
    __passThrough: PropTypes.object,
  };

  constructor(props) {
    super(props);

    const {
      componentSchema,
      store,
      action,
      parentXprops,
      ...extraProps
    } = props;

    // render schema might lose context, get it from action
    this.ctx = extraProps.context || (action && action.getContext());
    this.pageId = extraProps.pageId;
    this.componentName = componentSchema.componentName;
    this.componentView = this.ctx.getComponentView(this.componentName);
    this.extraProps = extraProps;

    this.xprops = XProps.create(
      store,
      parentXprops,
      action,
      componentSchema,
      this.componentView,
      extraProps
    );

    // 获取 ref 时需要一个固定的 function, 如果每次都是一个不同的匿名函数有时 react 会返回 null
    this.saveRef = this.saveRef.bind(this);
  }

  componentDidMount() {
    this.willDetach = this.xprops.onChange(() => {
      log('------------forceUpdate-----------');
      this.forceUpdate();
    });
  }

  componentWillReceiveProps(nextProps) {
    this.xprops.receiveProps(nextProps);
  }

  componentWillUnmount() {
    if (this.willDetach) {
      this.willDetach();
    }
    this.xprops.purge();
  }

  setInstance(fieldId, instance) {
    let key = fieldId;
    if (key) {
      const iteratorId = this.xprops.getIteratorId();
      if (iteratorId) {
        key = getJoinKey(iteratorId, key);
      }
      this.ctx.setComponentInstance(key, instance);
    }
    return this;
  }

  saveRef(instance) {
    const props = this.xprops.getProps();
    this.setInstance(props.fieldId, instance);
  }

  renderTry(View, props) {
    if (this.hasTryRender) { // 只验证一次, update 时忽略.
      return true;
    }
    const result = tryRender(View, props);
    if (result) {
      this.hasTryRender = true;
    }
    return result;
  }

  renderChildren(schema) {
    const { action } = this.props;
    const componentSchema = schema || this.props.componentSchema;
    const { children } = componentSchema;
    if (!children || !Array.isArray(children) || children.length < 1) {
      return {
        children,
        slots: [],
      };
    }

    const extraProps = {
      context: this.ctx,
      pageId: this.pageId,
      store: this.xprops.getStore(),
      action,
      parentXprops: this.xprops,
      __passThrough: this.xprops.getPassThrough(),
      __ownerScope: this.xprops.getClosestScope(),
      __iteratorId: this.xprops.getIteratorId(),
      __sectionRendered: this.xprops.isSectionRendered(),
    };
    const slots = {};
    let childrenList = children.filter(childSchema => childSchema.componentName !== 'Slot')
      .map((childSchema) => {
        let key = childSchema.id;
        if (extraProps.__iteratorId) {
          key = getJoinKey(extraProps.__iteratorId, key);
        }
        return (
          <Wrapper
            key={key}
            componentSchema={childSchema}
            {...extraProps}
          />
        );
      });
    children
      .filter(childSchema => childSchema.componentName === 'Slot')
      .forEach((childSchema) => {
        if (childSchema.props.slotName) {
          slots[childSchema.props.slotName] = (
            <Wrapper
              key={childSchema.id}
              componentSchema={childSchema}
              {...extraProps}
            />
          );
        }
      });
    // if a component declare a prop as __noWrapper we shall not render Wrapper
    // to wrap it but directly render the view with parsed props / slots
    if (componentSchema.props.__noWrapper) {
      childrenList = children.map((child) => {
        const View = this.ctx.getComponentView(child.componentName);
        const xprops = XProps.create(
          this.props.store,
          this.xprops,
          this.props.action,
          child,
          this.extraProps
        );
        const viewChildren = this.renderChildren(child);
        return (
          <View
            {...{ ...xprops.getProps(), ...viewChildren.slots }}
          >
            {viewChildren.children}
          </View>
        );
      });
    }

    return { children: childrenList || [], slots };
  }

  render() {
    const props = this.xprops.getProps();
    const { componentName } = this;
    const {
      componentSchema, __iteratorId, parentXprops, __ownerScope, __index,
    } = this.props;
    const View = this.componentView;
    if (!View) {
      warn(`The component '${componentName}' is undefined`);
      return null;
    }

    if (!isVisibleOnDevice(props.visibility, this.ctx.getDevice())) {
      log(`render a hidden ${componentName}`);
      return null;
    }

    this.ctx.collectRenderedComponent(`${componentName}@${View.version || 'null'}`);
    // 组件验证
    if (this.ctx.getRenderTry() && !this.renderTry(View, props)) {
      return (
        <RenderError message={`组件 ${componentName} 渲染错误`} />
      );
    }

    // 渲染元件
    if (this.componentName === 'Section'
      && props.sid
      && props.isSymbol
      // 如果是通过 renderSchema 调用被渲染的话视作循环渲染因此需要避免
      && !this.xprops.isSectionRendered()) {
      const { fn } = this.ctx;
      return fn.renderSection(props.sid, {
        ...props.options,
        key: props.scopeName,
      }, {
        ...componentSchema.props,
        parentXprops,
        __iteratorId,
        __ownerScope,
        __index,
      });
    }

    const { children, slots } = this.renderChildren();
    return (
      <View
        {...{ ...props, ...slots }}
        ref={this.saveRef}
      >
        {children}
      </View>
    );
  }
}

export default Wrapper;
